import React, {useEffect, useState, useRef} from 'react';
import {DataGrid, GridToolbar, useGridApiRef} from '@mui/x-data-grid';
import {
    Checkbox,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    DialogActions,
    Alert,
    AlertTitle,
    FormControlLabel,
    InputLabel,
    FormControl,
    Select,
    MenuItem,
    OutlinedInput,
    Box,
} from '@mui/material';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import {sendDataToApi} from '../../../../API/apiHelper';
import useApi from '../../../../hooks/useApi';
import {styled} from '@mui/system';
import Cookies from 'js-cookie';
import { FINANCIAL_PLATFORMS } from '../../../../constants/financialPlatforms';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 300,
        },
    },
};

function formatCurrency(value) {
    if (typeof value !== 'undefined' && value !== null) {
        return value.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
    } else {
        return '';
    }
}

function formatPersent(value) {
    if (typeof value !== 'undefined' && value !== null) {
        return value.toFixed(2);
    } else {
        return '';
    }
}

const StyledDataGrid = styled(DataGrid)(({theme}) => ({
    '& .MuiDataGrid-row': {
        '&:nth-child(even)': {
            backgroundColor: '#eaf0f5',
        },
    },
    '& .MuiDataGrid-columnHeaderTitle': {
        fontWeight: 'bold',
    },
    '& .negative.win_rate_per, .negative.Payout_per, .negative.fill_rate_per, .negative.profit_per': {
        backgroundColor: 'rgba(255,235,235)',
    },
    '& .positive.win_rate_per, .positive.Payout_per, .positive.fill_rate_per, .positive.profit_per': {
        backgroundColor: 'rgba(235,255,235)',
    },
    '& .MuiDataGrid-cell': {
        whiteSpace: 'normal !important',
        wordWrap: 'break-word',
    },
    '& .total-row': {
        backgroundColor: '#f8fff9',
        fontWeight: 'bold',
    },
}));

function processData(data) {
    if (!data || !Array.isArray(data) || data.length === 0) return [];

    const processedData = data.map(row => ({
        id: row.value.id,
        ...row.value,
        publisherDisplay: row.value.publisher ? row.value.publisher.name : '',
        publisherOriginal: row.value.publisher ? row.value.publisher.id : '',
        pubManagerDisplay: row.value.publisher_manager ? row.value.publisher_manager.name : '',
        pubManagerMail: row.value.publisher_manager ? row.value.publisher_manager.mail : '',
        pubManagerOriginal: row.value.publisher_manager ? row.value.publisher_manager.id : '',
        platformDisplay: row.value.platform ? row.value.platform.charAt(0).toUpperCase() + row.value.platform.slice(1) : '',
        irtb_statusDisplay: row.value.irtb_status ? row.value.irtb_status.charAt(0).toUpperCase() + row.value.irtb_status.slice(1) : '',
    }));

    if (processedData.length > 1) {
        const totalRow = processedData.shift();
        totalRow.total = true;
        processedData.push(totalRow);
    }

    return processedData;
}


const PayoutReportDataGrid = ({data, onRowEdit}) => {
    const gridRef = useRef(null);
    const [rows, setRows] = useState(processData(data));

    useEffect(() => {
        setRows(processData(data));
    }, [data]);

    const platform = rows.some(row => 'platform' in row);
    const publisher = rows.some(row => 'publisher' in row);
    const hyper_status = rows.some(row => 'hyper_status' in row);
    const irtb_status = rows.some(row => 'irtb_status' in row);
    const publisher_manager = rows.some(row => 'publisher_manager' in row);
    const hyper = rows.some(row => 'hyper' in row);

    const gross_total_final_payout = rows.some(row => 'gross_total_final_payout' in row);
    const approved_total_final_payout = rows.some(row => 'approved_total_final_payout' in row);
    const gross_total_deduction = rows.some(row => 'gross_total_deduction' in row);
    const approved_total_deduction = rows.some(row => 'approved_total_deduction' in row);
    const gross_total_revenue = rows.some(row => 'gross_total_revenue' in row);
    const approved_total_revenue = rows.some(row => 'approved_total_revenue' in row);
    const gross_total_profit = rows.some(row => 'gross_total_profit' in row);
    const approved_total_profit = rows.some(row => 'approved_total_profit' in row);
    const gross_profit_percentage = rows.some(row => 'gross_profit_percentage' in row);
    const approved_profit_percentage = rows.some(row => 'approved_profit_percentage' in row);
    const period = rows.some(row => 'DATE_FORMAT(period, \'%Y-%m\')' in row);
    const year = rows.some(row => 'DATE_FORMAT(period, \'%Y\')' in row);


    const columns = [];

    if (period) columns.push({field: 'DATE_FORMAT(period, \'%Y-%m\')', headerName: 'Period', width: 80});
    if (year) columns.push({field: 'DATE_FORMAT(period, \'%Y\')', headerName: 'Year', width: 80});
    if (publisher) columns.push({field: 'publisherDisplay', headerName: 'Publisher', width: 200});
    if (hyper) columns.push({field: 'hyper', headerName: 'Hyper ID', width: 100});
    {FINANCIAL_PLATFORMS.forEach((p) => {
        if (rows.some((row) => p.id in row)) columns.push({ field: p.id, headerName: `${p.name} ID`, width: 100 });
    })}
    if (publisher_manager) columns.push({field: 'pubManagerDisplay', headerName: 'Manager Name', width: 150});
    if (publisher_manager) columns.push({field: 'pubManagerMail', headerName: 'Manager Mail', width: 150});
    if (platform) columns.push({field: 'platformDisplay', headerName: 'Platform', width: 100});
    if (hyper_status) columns.push({field: 'hyper_status', headerName: 'Hyper Status', width: 150});
    if (irtb_status) columns.push({field: 'irtb_statusDisplay', headerName: 'iRTB Status', width: 100});

    if (gross_total_final_payout) columns.push({
        field: 'gross_total_final_payout',
        headerName: 'Gross Total Final Payout, $',
        width: 200,
        valueFormatter: (params) => `${formatCurrency(params.value)}`
    });
    if (approved_total_final_payout) columns.push({
        field: 'approved_total_final_payout',
        headerName: 'Approved Total Final Payout, $',
        width: 230,
        valueFormatter: (params) => `${formatCurrency(params.value)}`
    });
    if (gross_total_deduction) columns.push({
        field: 'gross_total_deduction',
        headerName: 'Gross Total Deduction, $',
        width: 200,
        valueFormatter: (params) => `${formatCurrency(params.value)}`
    });
    if (approved_total_deduction) columns.push({
        field: 'approved_total_deduction',
        headerName: 'Approved Total Deduction, $',
        width: 200,
        valueFormatter: (params) => `${formatCurrency(params.value)}`
    });
    if (gross_total_revenue) columns.push({
        field: 'gross_total_revenue',
        headerName: 'Gross Total Revenue, $',
        width: 200,
        valueFormatter: (params) => `${formatCurrency(params.value)}`
    });
    if (approved_total_revenue) columns.push({
        field: 'approved_total_revenue',
        headerName: 'Approved Total Revenue, $',
        width: 200,
        valueFormatter: (params) => `${formatCurrency(params.value)}`
    });
    if (gross_total_profit) columns.push({
        field: 'gross_total_profit',
        headerName: 'Gross Total Profit, $',
        width: 200,
        valueFormatter: (params) => `${formatCurrency(params.value)}`
    });
    if (approved_total_profit) columns.push({
        field: 'approved_total_profit',
        headerName: 'Approved Total Profit, $',
        width: 210,
        valueFormatter: (params) => `${formatCurrency(params.value)}`
    });
    if (gross_profit_percentage) columns.push({
        field: 'gross_profit_percentage',
        headerName: 'Gross Profit Percentage, %',
        width: 200,
        valueFormatter: (params) => `${formatPersent(params.value)}`
    });
    if (approved_profit_percentage) columns.push({
        field: 'approved_profit_percentage',
        headerName: 'Approved Profit Percentage, %',
        width: 220,
        valueFormatter: (params) => `${formatPersent(params.value)}`
    });

    if (data.length === 0) {
        return null;
    } else {

        return (

            <div style={{height: 800, width: '100%'}} ref={gridRef}>
                <StyledDataGrid
                    rows={rows}
                    columns={columns}
                    pageSize={25}
                    getRowClassName={(params) => (params.row.total ? 'total-row' : '')}
                    slots={{
                        toolbar: GridToolbar,
                    }}
                    disableRowSelectionOnClick
                />
            </div>
        );
    }
};

export default PayoutReportDataGrid;
